import { Button, IconButton, makeStyles, Paper, Typography } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import classNames from 'classnames';
import React, { ReactElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { AppContext } from 'context';
import { goBack } from 'utils/history';
import { UserRole } from '../../types';
import TooltipButton from '../Buttons/TooltipButton';

const useStyles = makeStyles({
  headerTitle: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  paper: {
    position: 'relative',
    flex: 1,
    marginBottom: '2em',
  },
  paperDefaultPadding: {
    padding: 50,
  },
  withButtonBar: {
    marginBottom: '6em',
  },
  title: {
    margin: '0',
  },
  paperHeader: {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    right: 0,
  },
  actionButton: {
    marginLeft: '1em',
  },
  buttonArea: {
    '& > *': {
      marginLeft: 10,
    },
  },
});

interface PageContentProps {
  noPadding?: boolean;
  withButtonBar?: boolean;
  title: string;
  backRoute?: string;
  actionButtonLink?: string | { pathname: string; state?: unknown };
  action?: 'create' | 'edit';
  paperClass?: string;
  exportButtonHandler?: () => any;
  exportButtonDisabled?: boolean;
  importButtonHandler?: (e: React.ChangeEvent<HTMLInputElement>) => any;
  importButtonDisabled?: boolean;
  customAction?: () => ReactElement;
  importAccept?: string;
}

const PageContent: React.FC<PageContentProps> = ({
  title,
  backRoute,
  actionButtonLink,
  action,
  children,
  noPadding,
  withButtonBar,
  exportButtonHandler,
  exportButtonDisabled = false,
  importButtonHandler,
  importButtonDisabled = false,
  importAccept = '.csv',
  customAction,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const roles = context.getRoles?.();
  const history = useHistory();

  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));

  return (
    <>
      <div className={classes.headerTitle}>
        <Typography variant="h1" className={classes.title}>
          {title}
        </Typography>
        <div className={classes.buttonArea}>
          {customAction && customAction()}
          {!readOnly && actionButtonLink && (
            <Button variant="outlined" component={Link} to={actionButtonLink} className={classes.actionButton}>
              {'edit' === action
                ? t('components.pageContent.editButtonLabel')
                : t('components.pageContent.createButtonLabel')}
            </Button>
          )}
          {!readOnly && importButtonHandler && (
            <Button
              variant="outlined"
              component="label"
              disabled={importButtonDisabled}
              className={classes.actionButton}
            >
              {t('components.pageContent.importButtonLabel')}
              <input type="file" name="import" accept={importAccept} hidden onChange={importButtonHandler} />
            </Button>
          )}
          {exportButtonHandler && (
            <TooltipButton
              tooltipText={exportButtonDisabled ? t('components.pageContent.exportButtonDisabled') : ''}
              variant="outlined"
              disabled={exportButtonDisabled}
              onClick={exportButtonHandler}
              className={classes.actionButton}
            >
              {t('components.pageContent.exportButtonLabel')}
            </TooltipButton>
          )}
        </div>
      </div>
      <Paper
        elevation={2}
        className={classNames(classes.paper, {
          [classes.paperDefaultPadding]: !noPadding,
          [classes.withButtonBar]: withButtonBar,
        })}
      >
        {backRoute && (
          <div className={classes.paperHeader}>
            <IconButton aria-label="close" className={classes.closeButton} onClick={() => goBack(backRoute, history)}>
              <Close />
            </IconButton>
          </div>
        )}
        {children}
      </Paper>
    </>
  );
};

export default PageContent;
